import { FC, useCallback, useContext, useMemo } from "react";
import Loading from "components/base/loading/Loading";
import { SportFavoritesContext, LiveFeedContext, MultiViewContext } from "@finbackoffice/site-core";
import { ISportFeedResponse } from "@finbackoffice/websocket-client";
import { useDefaultLiveParamsFromUrl } from "hooks";
import { usePathname, useRouter } from "next/navigation";
import { RouteNames } from "utils/constants";
import Button from "components/base/button/Button";
import classNames from "classnames";
import { Svg } from "components/base/svg/Svg";
import ActiveLink from "components/base/active-link/ActiveLink";
import Translate from "components/base/translate/Translate";
import { AssetsDirectory } from "@finbackoffice/enums";
import styles from "./advanced-sidebar-live.module.sass";
import AdvancedSidebarLiveSport from "./sport/AdvancedSidebarLiveSport";

const routeFavorites = `/${RouteNames.SPORT}/${RouteNames.LIVE}/${RouteNames.TOP}`;

const AdvancedSidebarLive: FC = () => {
    const pathname = usePathname();
    const router = useRouter();

    const { liveFeed, liveIsLoading, liveTopLeaguesGamesCount } = useContext(LiveFeedContext);
    const { favLiveLeaguesGameCount } = useContext(SportFavoritesContext);
    const { isSportSelected } = useContext(MultiViewContext) ?? {};

    const { sportSlug } = useDefaultLiveParamsFromUrl();

    const gamesCount = favLiveLeaguesGameCount + liveTopLeaguesGamesCount;

    const activeSportSlug = useMemo(() => {
        if (pathname.includes(`/${RouteNames.SPORT}/${RouteNames.LIVE}/${RouteNames.ALL}`)) {
            return sportSlug ?? liveFeed[0]?.slug;
        }
        return sportSlug;
    }, [liveFeed, pathname, sportSlug]);

    const handleFavoriteClick = () => {
        router.push(routeFavorites);
    };

    const renderSport = useCallback(
        (sport: ISportFeedResponse) => {
            const isSelected = activeSportSlug === sport.slug || isSportSelected(sport.id);
            return (
                <AdvancedSidebarLiveSport
                    key={sport.id}
                    sport={sport}
                    openedByDefault={isSelected}
                    isSelected={isSelected}
                />
            );
        },
        [activeSportSlug, isSportSelected],
    );

    return (
        <>
            <div className={styles.sportsNav}>
                <Button
                    type="custom"
                    disabled={gamesCount === 0}
                    className={classNames(styles.liveSportItem, {
                        [styles.selected]: pathname?.includes(routeFavorites),
                    })}
                    onClick={handleFavoriteClick}>
                    <>
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Tablet}/${AssetsDirectory.Sidebar}/favorite-events.svg`}
                            className="svg-icon"
                        />
                        <span>
                            <Translate tid="sport_favorite_events" />
                        </span>
                        <span className={styles.gamesCount}>{gamesCount}</span>
                    </>
                </Button>
                <ActiveLink
                    href={`/${RouteNames.SPORT}/${RouteNames.MULTI_VIEW}`}
                    activeClassName={styles.selected}
                    className={classNames(styles.liveSportItem)}>
                    <>
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Tablet}/${AssetsDirectory.Sidebar}/multiview.svg`}
                            className="svg-icon"
                        />
                        <span>
                            <Translate tid="pageName_multiView" />
                        </span>
                    </>
                </ActiveLink>
            </div>
            <div className={styles.sportsNav}>
                {!liveIsLoading ? <>{liveFeed.map(renderSport)}</> : <Loading />}
            </div>
        </>
    );
};

export default AdvancedSidebarLive;
